import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Users as UsersIcon,
  Search,
  Eye,
  RefreshCw,
  UserCheck,
  User,
  Building2,
  Mail,
  Phone
} from 'lucide-react';
import ColumnFilter from '../../components/shared/ColumnFilter';
import { toast } from 'sonner';
import api from '../../services/api';
import { getApiErrorMessage } from '../../utils/apiErrors';

/**
 * Users Page - Premium Design
 * Matches the Owner module's styling exactly
 */
const Users = () => {
  const [users, setUsers] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [columnFilters, setColumnFilters] = useState({
    name: '',
    email: '',
    role: []
  });

  // Debounce: re-fetch from server 400ms after searchTerm changes.
  // Also fires on mount (searchTerm = '') for the initial load.
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchUsers(searchTerm);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const fetchUsers = async (search = '') => {
    try {
      setLoading(true);

      const params = { limit: 100 };
      if (search) {
        params.search = search;
      }

      const { data } = await api.get('/admin/users', { params });
      setUsers(data.users || []);
      setTotalCount(data.total || 0);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to load users'));
    } finally {
      setLoading(false);
    }
  };

  // Apply filters — server handles searchTerm; column filters + role filter are client-side
  const filteredUsers = users.filter(user => {
    // matchesSearch always true — backend already filtered by searchTerm
    const matchesSearch = true;

    // Role filter
    const matchesRole = filterRole === 'all' ||
      user.role?.toUpperCase() === filterRole.toUpperCase();

    // Column filters
    const matchesName = !columnFilters.name ||
      user.name?.toLowerCase().includes(columnFilters.name.toLowerCase());

    const matchesEmail = !columnFilters.email ||
      user.email?.toLowerCase().includes(columnFilters.email.toLowerCase());

    const matchesRoleFilter = columnFilters.role.length === 0 ||
      columnFilters.role.includes(user.role?.toUpperCase());

    return matchesSearch && matchesRole && matchesName && matchesEmail && matchesRoleFilter;
  });

  // Calculate stats
  const stats = {
    total: totalCount,
    owners: users.filter(u => u.role?.toUpperCase() === 'OWNER' || u.role?.toUpperCase() === 'BUSINESS_OWNER').length,
    staff: users.filter(u => u.role?.toUpperCase() === 'STAFF').length
  };

  const getRoleBadge = (role) => {
    const roleUpper = (role || 'USER').toUpperCase();
    const badges = {
      'OWNER': { bg: 'bg-purple-100', text: 'text-purple-700' },
      'BUSINESS_OWNER': { bg: 'bg-purple-100', text: 'text-purple-700' },
      'ADMIN': { bg: 'bg-rose-100', text: 'text-rose-700' },
      'STAFF': { bg: 'bg-blue-100', text: 'text-blue-700' },
      'USER': { bg: 'bg-slate-100', text: 'text-slate-600' }
    };
    return badges[roleUpper] || badges.USER;
  };

  // Loading state
  if (loading) {
    return (
      <div className="h-full flex flex-col bg-white" data-testid="users-page">
        <div className="flex-shrink-0 bg-white border-b border-slate-200 shadow-sm">
          <div className="px-6 py-4">
            <h1 className="text-[18px] font-semibold text-slate-800">All Users</h1>
            <p className="text-[12px] text-slate-500 mt-0.5">Loading...</p>
          </div>
        </div>
        <div className="flex-1 flex items-center justify-center bg-[#F8F9FB]">
          <div className="flex flex-col items-center gap-3">
            <div className="w-10 h-10 border-2 border-rose-200 border-t-rose-500 rounded-full animate-spin" />
            <p className="text-slate-500 text-sm font-light">Loading users...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-white" data-testid="users-page">
      {/* ═══════════════════════════════════════════════════════════════════════
          PREMIUM HEADER
          ═══════════════════════════════════════════════════════════════════════ */}
      <div className="flex-shrink-0 bg-white border-b border-slate-200 shadow-sm">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-[18px] font-semibold text-slate-800">All Users</h1>
              <p className="text-[12px] text-slate-500 mt-0.5">Manage platform users</p>
            </div>
            <button
              onClick={() => fetchUsers(searchTerm)}
              className="flex items-center gap-2 px-4 py-2 bg-rose-600 text-white rounded-lg text-[13px] font-medium hover:bg-rose-700 transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════════════════
          STATS PILLS + FILTERS
          ═══════════════════════════════════════════════════════════════════════ */}
      <div className="flex-shrink-0 bg-[#F8F9FB] px-6 py-4">
        <div className="flex flex-wrap items-center justify-between gap-4">
          {/* Stats Pills */}
          <div className="flex flex-wrap items-center gap-3">
            {[
              { icon: UsersIcon, label: 'Total', value: stats.total, color: 'slate' },
              { icon: UserCheck, label: 'Owners', value: stats.owners, color: 'purple' },
              { icon: User, label: 'Staff', value: stats.staff, color: 'blue' },
            ].map((stat, idx) => {
              const colorMap = {
                slate: { bg: 'bg-slate-50', icon: 'text-slate-500', value: 'text-slate-600' },
                purple: { bg: 'bg-purple-50', icon: 'text-purple-500', value: 'text-purple-600' },
                blue: { bg: 'bg-blue-50', icon: 'text-blue-500', value: 'text-blue-600' },
              };
              const colors = colorMap[stat.color];

              return (
                <div key={idx} className="inline-flex items-center gap-3 px-4 py-2.5 bg-white rounded-xl shadow-sm border border-slate-100 hover:shadow-md transition-shadow duration-200">
                  <div className={`w-8 h-8 rounded-lg ${colors.bg} flex items-center justify-center`}>
                    <stat.icon className={`w-4 h-4 ${colors.icon}`} />
                  </div>
                  <div>
                    <p className="text-[10px] text-slate-400 uppercase tracking-wider font-medium">{stat.label}</p>
                    <p className={`text-[16px] font-bold ${colors.value}`}>{stat.value}</p>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Filters */}
          <div className="flex items-center gap-3">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="Search users..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 pr-4 py-2 border border-slate-200 rounded-lg text-[13px] w-56 focus:outline-none focus:ring-2 focus:ring-rose-200 focus:border-rose-400 bg-white"
              />
            </div>
            {/* Role Filter */}
            <select
              value={filterRole}
              onChange={(e) => setFilterRole(e.target.value)}
              className="px-3 py-2 border border-slate-200 rounded-lg text-[13px] focus:outline-none focus:ring-2 focus:ring-rose-200 focus:border-rose-400 bg-white"
            >
              <option value="all">All Roles</option>
              <option value="OWNER">Owners</option>
              <option value="STAFF">Staff</option>
              <option value="ADMIN">Admin</option>
              <option value="USER">No Business</option>
            </select>
          </div>
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════════════════
          TABLE AREA
          ═══════════════════════════════════════════════════════════════════════ */}
      <div className="flex-1 overflow-hidden flex flex-col px-4 pb-4 pt-2 bg-[#F8F9FB]">
        <div className="flex-1 flex flex-col bg-white rounded-xl border border-slate-200/60 shadow-sm overflow-hidden">
          {filteredUsers.length === 0 ? (
            <div className="flex-1 flex flex-col items-center justify-center">
              <div className="w-14 h-14 bg-slate-100 rounded-xl flex items-center justify-center mb-4">
                <UsersIcon className="w-7 h-7 text-slate-400" />
              </div>
              <p className="text-[15px] font-medium text-slate-600">
                {searchTerm ? 'No users found' : 'No users registered yet'}
              </p>
              <p className="text-[13px] text-slate-400 mt-1">
                {searchTerm ? 'Try adjusting your search' : 'Users will appear here when registered'}
              </p>
            </div>
          ) : (
            <div className="flex-1 flex flex-col overflow-hidden">
              {/* Single table with sticky header — fixes column alignment */}
              <div className="flex-1 overflow-auto">
                <table className="w-full table-fixed">
                  <colgroup>
                    <col style={{width: '22%'}} />
                    <col style={{width: '22%'}} />
                    <col style={{width: '14%'}} />
                    <col style={{width: '10%'}} />
                    <col style={{width: '12%'}} />
                    <col style={{width: '12%'}} />
                    <col style={{width: '8%'}} />
                  </colgroup>
                  <thead className="sticky top-0 z-10">
                    <tr className="border-b border-slate-100">
                      <th className="px-5 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider bg-gradient-to-r from-rose-50 to-red-50">
                        <div className="flex items-center gap-1">
                          Name
                          <ColumnFilter
                            type="text"
                            value={columnFilters.name}
                            onChange={(val) => setColumnFilters({...columnFilters, name: val})}
                            placeholder="Filter..."
                          />
                        </div>
                      </th>
                      <th className="px-5 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider bg-gradient-to-r from-rose-50 to-red-50">
                        <div className="flex items-center gap-1">
                          <Mail className="w-3.5 h-3.5" />
                          Email
                          <ColumnFilter
                            type="text"
                            value={columnFilters.email}
                            onChange={(val) => setColumnFilters({...columnFilters, email: val})}
                            placeholder="Filter..."
                          />
                        </div>
                      </th>
                      <th className="px-5 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider bg-gradient-to-r from-rose-50 to-red-50">
                        <div className="flex items-center gap-1">
                          <Phone className="w-3.5 h-3.5" />
                          Phone
                        </div>
                      </th>
                      <th className="px-5 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider bg-gradient-to-r from-rose-50 to-red-50">
                        <div className="flex items-center gap-1">
                          Role
                          <ColumnFilter
                            type="status"
                            value={columnFilters.role}
                            onChange={(val) => setColumnFilters({...columnFilters, role: val})}
                            options={[
                              { value: 'OWNER', label: 'Owner' },
                              { value: 'STAFF', label: 'Staff' },
                              { value: 'ADMIN', label: 'Admin' },
                              { value: 'USER', label: 'No Business' }
                            ]}
                          />
                        </div>
                      </th>
                      <th className="px-5 py-3 text-center text-[11px] font-semibold text-slate-500 uppercase tracking-wider bg-gradient-to-r from-rose-50 to-red-50">
                        <div className="flex items-center justify-center gap-1">
                          <Building2 className="w-3.5 h-3.5" />
                          Businesses
                        </div>
                      </th>
                      <th className="px-5 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider bg-gradient-to-r from-rose-50 to-red-50">
                        Created
                      </th>
                      <th className="px-5 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider bg-gradient-to-r from-rose-50 to-red-50">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {filteredUsers.map((user, index) => {
                      const roleBadge = getRoleBadge(user.role);
                      return (
                        <tr
                          key={user.id}
                          className={`hover:bg-rose-50/30 transition-colors ${index % 2 === 0 ? 'bg-white' : 'bg-[#FAFBFC]'}`}
                        >
                          <td className="px-5 py-3">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 bg-slate-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                <User className="w-4 h-4 text-slate-500" />
                              </div>
                              <div className="min-w-0">
                                <div className="text-[13px] font-medium text-slate-800 truncate">{user.name || 'N/A'}</div>
                                <div className="text-[11px] text-slate-400 truncate">
                                  {user.status === 'active' ? 'Active' : 'Inactive'}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-5 py-3 text-[13px] text-slate-600 truncate">
                            {user.email || 'N/A'}
                          </td>
                          <td className="px-5 py-3 text-[13px] text-slate-600">
                            {user.phone || 'N/A'}
                          </td>
                          <td className="px-5 py-3">
                            <span className={`px-2 py-1 rounded text-[11px] font-medium ${roleBadge.bg} ${roleBadge.text}`}>
                              {user.role || 'User'}
                            </span>
                          </td>
                          <td className="px-5 py-3 text-[13px] text-slate-600 text-center">
                            {user.business_count || 0}
                          </td>
                          <td className="px-5 py-3 text-[12px] text-slate-500">
                            {user.created_at ? new Date(user.created_at).toLocaleDateString() : '-'}
                          </td>
                          <td className="px-5 py-3">
                            <div className="flex items-center gap-1">
                              <Link to={`/admin/users/${user.id}`}>
                                <button
                                  className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                                  title="View Details"
                                >
                                  <Eye className="w-4 h-4 text-slate-600" />
                                </button>
                              </Link>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Footer */}
              <div className="flex-shrink-0 px-5 py-3 bg-slate-50/80 border-t border-slate-100 flex items-center justify-between text-[12px] text-slate-500">
                <p>
                  Showing <span className="font-medium text-slate-700">{filteredUsers.length}</span> of <span className="font-medium text-slate-700">{totalCount}</span> users
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Users;
